/**
 * Canonical display formats for ShowTimeBro (India).
 *
 * Date only:  16 Oct 2026
 * Date+time:  16 Oct 2026 • 01:30 pm
 *
 * - Timestamps (createdAt, paidAt, booking time) → Asia/Kolkata
 * - Event schedule wall-clock stored as UTC face → timezone "UTC"
 */

export const TZ_IST = "Asia/Kolkata";

function asDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // YYYY-MM-DD → treat as calendar day (noon UTC avoids DST edge)
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const d = new Date(`${value.trim()}T12:00:00.000Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtParts(d, timeZone, withTime) {
  const date = new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone,
  }).format(d);

  if (!withTime) return date;

  const time = new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).format(d);

  return `${date} • ${time}`;
}

/** Timestamp → IST date only */
export function formatDateIST(value) {
  const d = asDate(value);
  if (!d) return "—";
  return fmtParts(d, TZ_IST, false);
}

/** Timestamp → IST date + time */
export function formatDateTimeIST(value) {
  const d = asDate(value);
  if (!d) return "—";
  return fmtParts(d, TZ_IST, true);
}

/**
 * Event / show wall-clock (ISO stored as UTC face — do not shift to IST).
 * Date only.
 */
export function formatEventDate(value) {
  const d = asDate(value);
  if (!d) return "—";
  return fmtParts(d, "UTC", false);
}

/** Event / show wall-clock date + time (UTC face). */
export function formatEventDateTime(value) {
  const d = asDate(value);
  if (!d) return "—";
  return fmtParts(d, "UTC", true);
}

/** Event wall-clock parts (UTC face) for ticket UI. */
export function formatEventDateTimeParts(value) {
  const d = asDate(value);
  if (!d) return { date: "", time: "" };
  const date = new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
  const time = new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  }).format(d);
  return { date, time };
}

/**
 * Flexible formatter used by booking lists.
 * @param {"ist"|"utc"} zone
 */
export function formatDateTime(
  value,
  { withTime = true, zone = "ist" } = {}
) {
  const d = asDate(value);
  if (!d) return "—";
  const timeZone = zone === "utc" ? "UTC" : TZ_IST;
  return fmtParts(d, timeZone, withTime);
}

/** Alias matching older local helpers */
export const formatDDMonYYYY = formatEventDate;
export const formatDDMonYYYY_HHMM = formatDateTimeIST;
